import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTourStore } from '@/stores/tourStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useAuthStore } from '@/stores/authStore';
import { onboardingPreAnalyzeSteps, getToolIntroPreGenSteps, toolOrientationSteps } from './tourSteps';
import { getTourTier } from '@/lib/tourTier';
import { startTourWithMobileMenu } from './tourMobileMenu';

/**
 * WelcomeModal — first-touch tour invitation. Reused for both tour phases:
 * the page that opens it sets `pendingPhase` on the store first, and this
 * modal starts that phase (with the right steps) when the user accepts.
 *
 * Open/close state is read from `tourStore` (`showWelcomeModal`).
 */
export default function WelcomeModal() {
  const showWelcomeModal = useTourStore((s) => s.showWelcomeModal);
  const pendingPhase = useTourStore((s) => s.pendingPhase);
  const startTour = useTourStore((s) => s.startTour);
  const setSteps = useTourStore((s) => s.setSteps);
  const closeWelcomeModal = useTourStore((s) => s.closeWelcomeModal);
  const user = useAuthStore((s) => s.user);

  // Derive the subscription tier — drives tier-aware copy in the tool tour.
  // Shared with Tool.tsx so the same user sees the same copy from either path.
  const tier = getTourTier(user);

  const handleShowAround = () => {
    const phase = pendingPhase ?? 'onboarding';
    // Load the steps for the phase being started, then drive the tour.
    // The WelcomeModal only ever opens the ENTRY phases of the journey — the
    // post-analyze and post-gen continuations, and every chapter in between,
    // re-fire automatically down the chain, never via this modal.
    //   /onboarding → 'onboarding'        (build the workspace)
    //   /tool       → 'tool_orientation'  (name the room, then walk the app)
    if (phase === 'tool_orientation') {
      setSteps(toolOrientationSteps);
      startTour(phase);
    } else if (phase === 'tool_intro') {
      setSteps(getToolIntroPreGenSteps(tier));
      // On mobile the platform-sidebar anchor only mounts when the menu is open
      // — open it before the tour resolves steps, or the step is silently
      // dropped. No-op on desktop / non-tool pages.
      startTourWithMobileMenu(() => startTour(phase));
    } else {
      setSteps(onboardingPreAnalyzeSteps);
      startTour(phase);
    }
    closeWelcomeModal();
  };

  const handleExplore = () => {
    // "Maybe later" — record a dismissal so the tour won't auto-open again,
    // but don't mark the tutorial fully completed.
    void usePreferencesStore.getState().updatePreferences({
      tutorial_dismissed_at: new Date().toISOString(),
    });
    closeWelcomeModal();
  };

  return (
    <Dialog
      open={showWelcomeModal}
      onOpenChange={(open) => {
        // Soft dismiss (X / outside click): just close, don't persist.
        if (!open) closeWelcomeModal();
      }}
    >
      <DialogContent className="max-w-md mx-4 bg-white border border-hairline rounded-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-ink text-xl">Welcome to Zupa 👋</DialogTitle>
          <DialogDescription className="text-base pt-1 text-steel-dark">
            Want a quick guided tour, or you'd rather poke around yourself?
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 pt-4">
          <Button
            onClick={handleShowAround}
            size="lg"
            className="w-full bg-cargo hover:bg-cargo/90 text-cargo-ink rounded-md focus-visible:ring-ink"
          >
            Show me around
          </Button>
          <Button
            onClick={handleExplore}
            size="lg"
            variant="outline"
            className="w-full border-hairline text-steel-dark hover:bg-paper rounded-md"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
